import { useEffect, useState } from 'react';
import { Alert, App, Button, Card, Checkbox, Popconfirm, Progress, Segmented, Space, Table, Tag, Typography } from 'antd';
import { MergeCellsOutlined, ReloadOutlined, SearchOutlined, StopOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { DuplicateCandidateItem } from '@crm/shared';
import { MERGE_SELECTED_TIER, REASON_LABELS } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { errorMessage } from '../../api/client';
import { useDismissByReasons, useDismissCandidate, useDismissCandidates, useUnmergeCandidate, useDuplicateReasons, useDuplicateSummary, useDuplicates, useLatestMassMerge, useMergeSelected, useResetScan, useScanDuplicates, useStopScan } from '../../features/dedupe/api';
import { MergeDialog } from './MergeDialog';
import { MassMergeDialog } from './MassMergeDialog';
import { NameCell } from '../../components/NameCell';
import { EmptyState, tableLoading, tableLocale } from '../../components/states';
import { fmtDate, plural } from '../../utils/format';

export function DuplicatesPage() {
  const { message } = App.useApp();
  const [objectType, setObjectType] = useState<'CONTACT' | 'ORGANIZATION'>('CONTACT');
  const [status, setStatus] = useState('PENDING');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [reviewing, setReviewing] = useState<string | null>(null);
  const [massMerging, setMassMerging] = useState(false);
  // pending pairs with exactly these reasons (comma-separated), or all
  const [reasons, setReasons] = useState<string | null>(null);
  // pending pairs ticked in the table (kept across pages)
  const [selected, setSelected] = useState<string[]>([]);
  const massRun = useLatestMassMerge().data;
  // the scan runs on the worker; poll for a short while after queuing it
  const [pollUntil, setPollUntil] = useState(0);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (pollUntil <= now) return;
    const t = window.setTimeout(() => setNow(Date.now()), 1000);
    return () => window.clearTimeout(t);
  }, [pollUntil, now]);
  const polling = pollUntil > now;
  const summary = useDuplicateSummary(polling);
  // a full scan can outlast the first 20 seconds: keep refreshing while the server says it is scanning
  const scanning = !!summary.data?.scanning;
  useEffect(() => {
    if (scanning) setPollUntil((t) => Math.max(t, Date.now() + 10_000));
  }, [scanning, summary.dataUpdatedAt]);
  const dups = useDuplicates({ objectType, status, page, pageSize, reasons: status === 'PENDING' ? (reasons ?? undefined) : undefined }, polling);
  const reasonGroups = useDuplicateReasons(objectType, status === 'PENDING', polling);
  const dismissByReasons = useDismissByReasons();
  const selectedGroup = reasons ? reasonGroups.data?.find((g) => g.reasons.join(',') === reasons) : undefined;
  const reasonLabel = (rs: string[]) => rs.map((r) => REASON_LABELS[r] ?? r).join(' + ');
  const scan = useScanDuplicates();
  const stopScan = useStopScan();
  const resetScan = useResetScan();
  const [resetDismissed, setResetDismissed] = useState(false);
  // a queued scan can be stopped too, until the worker has had time to pick it up
  const [queuedAt, setQueuedAt] = useState(0);
  const startPolling = (queued = false) => {
    setPollUntil(Date.now() + 20_000);
    setNow(Date.now());
    setQueuedAt(queued ? Date.now() : 0);
  };
  const dismiss = useDismissCandidate();
  const unmerge = useUnmergeCandidate();
  const dismissMany = useDismissCandidates();
  const mergeSelected = useMergeSelected();
  const linkFor = (id: string) => (objectType === 'CONTACT' ? `/contacts/${id}` : `/organizations/${id}`);

  return (
    <div className="page">
      <PageHeader
        title="Duplicates"
        subtitle={<span>Contacts with the same email are merged automatically. Likely matches by phone, name and similar email wait here for review. {summary.data?.lastScanAt ? `Last scan ${dayjs(summary.data.lastScanAt).fromNow()}.` : 'A scan runs nightly at 03:00.'}</span>}
        extra={
          <Space>
          <Button type="primary" icon={<MergeCellsOutlined />} onClick={() => setMassMerging(true)}>
            Mass merge
          </Button>
          <Button icon={<SearchOutlined />} loading={scan.isPending || scanning} onClick={() => scan.mutateAsync().then(() => { startPolling(true); message.success('Scan queued; results appear in a moment'); }).catch((e) => message.error(errorMessage(e)))}>
            {scanning ? 'Scanning' : 'Scan now'}
          </Button>
          {(scanning || now - queuedAt < 5_000) && (
            <Button danger icon={<StopOutlined />} loading={stopScan.isPending} onClick={() => stopScan.mutateAsync().then((r) => { startPolling(); message.success(r.stopped ? 'Scan stopped. Pairs found so far are kept.' : 'No scan is running'); }).catch((e) => message.error(errorMessage(e)))}>
              Stop scan
            </Button>
          )}
          <Popconfirm
            title="Start the duplicate search over?"
            description={
              <div style={{ maxWidth: 320 }}>
                <Typography.Paragraph style={{ marginBottom: 8 }}>Stops any running scan, deletes every pair waiting for review and scans all contacts and organizations again. Merged pairs stay in the history.</Typography.Paragraph>
                <Checkbox checked={resetDismissed} onChange={(e) => setResetDismissed(e.target.checked)}>Also delete dismissed pairs, so they can be suggested again</Checkbox>
              </div>
            }
            okText="Start over"
            okButtonProps={{ danger: true, loading: resetScan.isPending }}
            onOpenChange={(o) => o && setResetDismissed(false)}
            onConfirm={() => resetScan.mutateAsync({ includeDismissed: resetDismissed }).then((r) => { setSelected([]); setReasons(null); setPage(1); startPolling(true); message.success(`Deleted ${plural(r.deleted, 'pair')}; a new scan is running`); }).catch((e) => message.error(errorMessage(e)))}
          >
            <Button icon={<ReloadOutlined />}>Start over</Button>
          </Popconfirm>
          </Space>
        }
      />
      {massRun?.status === 'RUNNING' && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          title={
            <Space wrap>
              <span>{massRun.tier === MERGE_SELECTED_TIER ? 'Merging selected pairs' : `Mass merge (tier ${massRun.tier}) running`}: {massRun.mergedGroups.toLocaleString()} groups merged</span>
              <Progress style={{ width: 200, margin: 0 }} size="small" percent={massRun.totalGroups ? Math.floor(((massRun.mergedGroups + massRun.conflictGroups + massRun.skippedGroups + massRun.failedGroups) / massRun.totalGroups) * 100) : 0} />
            </Space>
          }
          action={<Button size="small" onClick={() => setMassMerging(true)}>Details</Button>}
        />
      )}
      <Card>
      <div className="table-toolbar">
        <div className="toolbar-group">
        <Segmented value={objectType} onChange={(v) => { setObjectType(v as 'CONTACT' | 'ORGANIZATION'); setPage(1); setReasons(null); setSelected([]); }} options={[{ value: 'CONTACT', label: `Contacts${summary.data ? ` (${plural(summary.data.pendingContacts, 'pair')})` : ''}` }, { value: 'ORGANIZATION', label: `Organizations${summary.data ? ` (${plural(summary.data.pendingOrganizations, 'pair')})` : ''}` }]} />
        </div>
        <div className="toolbar-group">
        <Segmented value={status} onChange={(v) => { setStatus(String(v)); setPage(1); setReasons(null); setSelected([]); }} options={[{ value: 'PENDING', label: 'To review' }, { value: 'MERGED', label: 'Merged' }, { value: 'DISMISSED', label: 'Dismissed' }]} />
        </div>
      </div>
      {status === 'PENDING' && summary.data && (objectType === 'CONTACT' ? summary.data.pendingContacts : summary.data.pendingOrganizations) > 0 && (
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          {objectType === 'CONTACT'
            ? `${plural(summary.data.pendingContacts, 'possible duplicate pair')} involving ${plural(summary.data.pendingContactRecords, 'contact')}.`
            : `${plural(summary.data.pendingOrganizations, 'possible duplicate pair')} involving ${plural(summary.data.pendingOrganizationRecords, 'organization')}.`}{' '}
          Each row is one pair, and one record can be in many pairs: 30 colleagues sharing a phone number make 435 pairs.
        </Typography.Paragraph>
      )}
      {status === 'PENDING' && !!reasonGroups.data?.length && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Typography.Text type="secondary">Why:</Typography.Text>
          <Tag.CheckableTag checked={!reasons} onChange={() => { setReasons(null); setPage(1); setSelected([]); }}>All</Tag.CheckableTag>
          {reasonGroups.data.map((g) => {
            const key = g.reasons.join(',');
            return (
              <Tag.CheckableTag key={key} checked={reasons === key} onChange={() => { setReasons(reasons === key ? null : key); setPage(1); setSelected([]); }}>
                {reasonLabel(g.reasons)} ({g.count.toLocaleString()})
              </Tag.CheckableTag>
            );
          })}
          {selectedGroup && (
            <Popconfirm
              title={`Dismiss all ${plural(selectedGroup.count, 'pair')} found by “${reasonLabel(selectedGroup.reasons)}”?`}
              description="They won't be suggested again and a mass merge leaves them apart. No contact is changed."
              okText="Dismiss all"
              okButtonProps={{ danger: true, loading: dismissByReasons.isPending }}
              onConfirm={() =>
                dismissByReasons
                  .mutateAsync({ objectType, reasons: selectedGroup.reasons })
                  .then((r) => { message.success(`${plural(r.dismissed, 'pair')} dismissed`); setReasons(null); setPage(1); setSelected([]); })
                  .catch((e) => message.error(errorMessage(e)))
              }
            >
              <Button size="small" danger style={{ marginLeft: 'auto' }}>Dismiss all {selectedGroup.count.toLocaleString()}</Button>
            </Popconfirm>
          )}
        </div>
      )}
      {status === 'PENDING' && selected.length > 0 && (
        <div className="bulk-bar" style={{ background: '#EEF2FE', borderRadius: 10, padding: '8px 12px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Typography.Text strong>{plural(selected.length, 'pair')} selected</Typography.Text>
          <Space>
            {objectType === 'CONTACT' && (
              <Popconfirm
                title={`Merge ${plural(selected.length, 'selected pair')}?`}
                description={
                  <div style={{ maxWidth: 360 }}>
                    Pairs that share a contact are merged together into the oldest record. Emails, phone numbers, tags, lists, segments and history are combined; where the records hold different values, the oldest record’s value is kept and the others stay in the merge snapshot on the timeline.
                  </div>
                }
                okText="Merge"
                okButtonProps={{ loading: mergeSelected.isPending }}
                disabled={massRun?.status === 'RUNNING'}
                onConfirm={() =>
                  mergeSelected
                    .mutateAsync(selected)
                    .then(() => { setSelected([]); setMassMerging(true); })
                    .catch((e) => message.error(errorMessage(e)))
                }
              >
                <Button size="small" type="primary" disabled={massRun?.status === 'RUNNING'} title={massRun?.status === 'RUNNING' ? 'Wait for the running merge to finish' : undefined}>
                  Merge selected
                </Button>
              </Popconfirm>
            )}
            <Popconfirm
              title={`Dismiss ${plural(selected.length, 'selected pair')}?`}
              description="They won't be suggested again and a mass merge leaves them apart. No contact is changed."
              okText="Dismiss"
              okButtonProps={{ danger: true, loading: dismissMany.isPending }}
              onConfirm={() =>
                dismissMany
                  .mutateAsync({ ids: selected })
                  .then((r) => { message.success(`${plural(r.dismissed, 'pair')} dismissed`); setSelected([]); })
                  .catch((e) => message.error(errorMessage(e)))
              }
            >
              <Button size="small" danger>Dismiss selected</Button>
            </Popconfirm>
            <Button size="small" type="link" onClick={() => setSelected([])}>
              Clear selection
            </Button>
          </Space>
        </div>
      )}
      <Table<DuplicateCandidateItem>
        rowKey="id"
        rowSelection={status === 'PENDING' ? { selectedRowKeys: selected, onChange: (keys) => setSelected(keys as string[]), preserveSelectedRowKeys: true } : undefined}
        loading={tableLoading(dups)}
        scroll={{ x: 'max-content' }}
        locale={tableLocale(
          dups,
          status === 'PENDING' ? (
            <EmptyState description={`No ${objectType === 'CONTACT' ? 'contact' : 'organization'} duplicates to review. Run a scan to check again.`} />
          ) : status === 'MERGED' ? (
            'No merged duplicates yet.'
          ) : (
            'No dismissed suggestions.'
          ),
        )}
        dataSource={dups.data?.items ?? []}
        pagination={{ current: page, pageSize, total: dups.data?.total ?? 0, showSizeChanger: true, onChange: (p, s) => { setPage(s === pageSize ? p : 1); setPageSize(s); } }}
        columns={[
          { title: 'Record A', key: 'a', render: (_, c) => <NameCell name={c.a.label} sub={[c.a.email ?? c.a.domain, c.a.organization].filter(Boolean).join(' · ') || undefined} to={linkFor(c.a.id)} /> },
          { title: 'Record B', key: 'b', render: (_, c) => <NameCell name={c.b.label} sub={[c.b.email ?? c.b.domain, c.b.organization].filter(Boolean).join(' · ') || undefined} to={linkFor(c.b.id)} /> },
          { title: 'Why', key: 'why', render: (_, c) => <Space size={4} wrap>{c.reasons.map((r) => <Tag key={r}>{REASON_LABELS[r] ?? r}</Tag>)}</Space> },
          { title: 'Score', dataIndex: 'score', width: 90, render: (s: number) => <Tag color={s >= 0.9 ? 'error' : s >= 0.75 ? 'warning' : 'default'}>{Math.round(s * 100)}%</Tag> },
          { title: 'Found', dataIndex: 'createdAt', width: 120, render: (v) => fmtDate(v) },
          {
            key: 'actions',
            align: 'right',
            width: 190,
            render: (_, c) =>
              c.status === 'PENDING' ? (
                <Space>
                  <Button size="small" type="primary" onClick={() => setReviewing(c.id)}>Review & merge</Button>
                  <Popconfirm title="Not a duplicate? It won't be suggested again." okText="Dismiss" okButtonProps={{ loading: dismiss.isPending }} onConfirm={() => dismiss.mutateAsync(c.id).then(() => message.success('Suggestion dismissed')).catch((e) => message.error(errorMessage(e)))}>
                    <Button size="small">Dismiss</Button>
                  </Popconfirm>
                </Space>
              ) : (
                <Space>
                  <Tag color={c.status === 'MERGED' ? 'success' : 'default'}>{c.status === 'MERGED' ? 'Merged' : 'Dismissed'}{c.resolvedAt ? ` ${fmtDate(c.resolvedAt)}` : ''}</Tag>
                  {c.unmerge && (
                    <Popconfirm
                      title="Unmerge these contacts?"
                      description={
                        <div style={{ maxWidth: 360 }}>
                          {c.unmerge.full
                            ? 'The retired contact comes back with its emails, phone numbers, organizations, tags, lists, deals, tasks and history. The other contact gets its earlier values back where nobody edited them since.'
                            : 'This merge was done before merges were recorded in full: the contact comes back with its own details, emails, phone numbers, organizations, tags and lists, but its deals, tasks and history stay with the other contact.'}{' '}
                          The pair is then marked as not a duplicate.
                        </div>
                      }
                      okText="Unmerge"
                      okButtonProps={{ loading: unmerge.isPending }}
                      onConfirm={() =>
                        unmerge
                          .mutateAsync(c.id)
                          .then((r) => message.success(r.partial ? 'Unmerged. Deals, tasks and history stayed with the other contact.' : 'Unmerged'))
                          .catch((e) => message.error(errorMessage(e)))
                      }
                    >
                      <Button size="small">Unmerge</Button>
                    </Popconfirm>
                  )}
                </Space>
              ),
          },
        ]}
      />
      </Card>
      <MergeDialog candidateId={reviewing} onClose={() => setReviewing(null)} />
      <MassMergeDialog open={massMerging} onClose={() => setMassMerging(false)} />
    </div>
  );
}
